import { Sine, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as Topics from "../Data/Topics";
import { DebugBalloonControls } from "../GameObjects/DebugBalloonControls";
import { HealthBar } from "../GameObjects/HealthBar";
import { LiftButton } from "../GameObjects/LiftButton";
import { ProgressBar } from "../GameObjects/ProgressBar";

export enum UIConfiguration {
    GAMEPLAY,
    ONBOARDING,
    NUM_ELEMENTS,
}

export class UI extends PIXI.Container {
    private _config: UIConfiguration;

    private _healthBar: HealthBar;
    private _progress: ProgressBar;
    private _balloonControls: DebugBalloonControls;
    private _buttonUp: LiftButton;
    private _arrow: PIXI.Sprite;
    private _keyboardPrompt: PIXI.Sprite;

    private _arrowAlphaTween: TweenLite | undefined;
    private _arrowPosTween: TweenMax | undefined;
    private _keyboardTween: TweenLite | undefined;
    private _subscriptions: any[];

    constructor(pConfig: UIConfiguration) {
        super();
        this._config = pConfig;

        this._healthBar = new HealthBar();
        this.addChild(this._healthBar);

        this._progress = new ProgressBar();
        this.addChild(this._progress);

        this._buttonUp = new LiftButton();
        this.addChild(this._buttonUp);

        this._balloonControls = new DebugBalloonControls();
        this.addChild(this._balloonControls);

        this._arrow = HLF.PixiUtils.makeSprite("onboarding-arrow", Constants.SPRITESHEET_UI);
        this._arrow.anchor.set(0.5, 1.0);
        this._arrow.visible = false;
        this.addChild(this._arrow);

        this._keyboardPrompt = HLF.PixiUtils.makeSprite("keyboard-prompt", Constants.SPRITESHEET_GAMEPLAY);
        this._keyboardPrompt.visible = false;
        this.addChild(this._keyboardPrompt);

        this._subscriptions = new Array<any>();
        this._subscriptions.push(PubSub.subscribe(
            Topics.SHOW_ONBOARDING_ARROW,
            this.showOnboardingArrow.bind(this),
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.HIDE_ONBOARDING_ARROW,
            this.hideOnboardingArrow.bind(this),
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.SHOW_ONBOARDING_KEYBOARD,
            this.showOnboardingKeyboard.bind(this),
        ));
        this._subscriptions.push(PubSub.subscribe(
            Topics.HIDE_ONBOARDING_KEYBOARD,
            this.hideOnboardingKeyboard.bind(this),
        ));
    }

    public update(pDeltaTime: number): void {
        this._buttonUp.update(pDeltaTime);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        if (this._arrowPosTween !== undefined) { this._arrowPosTween.kill(); }
        if (this._arrowAlphaTween !== undefined) { this._arrowAlphaTween.kill(); }
        if (this._keyboardTween !== undefined) { this._keyboardTween.kill(); }
        for (let i = 0; i < this._subscriptions.length; ++i) {
            PubSub.unsubscribe(this._subscriptions[i]);
        }
        super.destroy(pOptions);
    }

    public onResize(pSize: PIXI.Point): void {
        switch (this._config) {
            case UIConfiguration.GAMEPLAY:
                // Offset position of health + progress bar to make room for up button on narrow screens
                const offsetRange: PIXI.Point = new PIXI.Point(1024, 1224);
                const offsetMax: number = 80;
                const offsetPerc: number = Math.max(0, (offsetRange.y - pSize.x) / (offsetRange.y - offsetRange.x));
                const offset: number = offsetPerc * offsetMax;
                this._healthBar.position.set(-212 + offset, pSize.y * 0.5 - 25);
                this._progress.position.set(212 + offset, pSize.y * 0.5 - 25);
                this._balloonControls.position.set(pSize.x * 0.5 - 250, -pSize.y * 0.5);
                this._buttonUp.position.set(
                    -pSize.x * 0.5 + this._buttonUp.width * 0.5 + 10,
                    pSize.y * 0.5 - this._buttonUp.height * 0.5 - 10,
                );
                break;
            case UIConfiguration.ONBOARDING:
                this._healthBar.position.set(0, pSize.y * 0.5 - 25);
                this._progress.position.set(0, pSize.y * 1.0);
                this._balloonControls.position.set(pSize.x * 0.5 - 250, -pSize.y * 0.5);
                this._buttonUp.position.set(
                    -pSize.x * 0.5 + this._buttonUp.width * 0.5 + 10,
                    pSize.y * 0.5 - this._buttonUp.height * 0.5 - 10,
                );
                break;
        }
    }

    private showOnboardingArrow(): void {
        this._arrow.position.copy(this._buttonUp.position);
        this._arrow.position.y -= 80;
        this._arrow.alpha = 0;
        this._arrow.visible = true;
        this._arrow.anchor.y = 1.4;
        this._arrowAlphaTween = TweenLite.to(this._arrow, 0.5, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
        this._arrowPosTween = TweenMax.to(this._arrow.anchor, 1.0, {
            y: 1.0,
            ease: Sine.easeInOut,
            repeat: -1,
            yoyo: true,
        });
    }

    private hideOnboardingArrow(): void {
        if (this._arrowPosTween !== undefined) {
            if (this._arrowPosTween !== undefined) { this._arrowPosTween.kill(); }
            if (this._arrowAlphaTween !== undefined) { this._arrowAlphaTween.kill(); }
            this._arrowAlphaTween = TweenLite.to(this._arrow, 0.5, {
                alpha: 0,
                ease: Sine.easeInOut,
                onComplete: () => { this._arrow.visible = false; },
            });
        }
    }

    private showOnboardingKeyboard(): void {
        this._keyboardPrompt.alpha = 0;
        this._keyboardPrompt.visible = true;

        this._keyboardTween = TweenLite.to(this._keyboardPrompt, 0.5, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
    }

    private hideOnboardingKeyboard(): void {
        if (this._keyboardTween !== undefined) {
            this._keyboardTween.kill();

            this._keyboardTween = TweenLite.to(this._keyboardPrompt, 0.5, {
                alpha: 0,
                ease: Sine.easeInOut,
                onComplete: () => { this._keyboardPrompt.visible = false; },
            });
        }
    }
}
